import _get from 'lodash/get'
import * as z from 'zod'

import {
  v2ContinueFooter,
  v2PetPortrait,
  v2Text,
  v2TextWithPetName,
} from '@flows/configs/petHealthV2/steps/elements/shared'
import {
  listedConditions,
  resolveStepAfterConditionsQuestion,
} from '@flows/configs/petHealthV2/utils'
import * as schemaUtils from '@flows/configs/shared/schemaUtils'
import type { StepConfigParams } from '@flows/configs/types'
import type { AnyStepConfiguration, FlowValues } from '@flows/core/types/stepTypes'

const NATIVE_INFO_TEXT = { style: 'caption', color: 'grey700', emphasis: 'default' } as const

const pre_existing_conditions = (params: StepConfigParams, platform: 'native' | 'web') => {
  const { getParamPath, getTransientPath } = params.helperFunctions
  const hasConditionsPath = getTransientPath('has_pre_existing_conditions')
  const conditionsPath = getParamPath('health_status_entries')
  const title = {
    type: 'title',
    text: v2TextWithPetName(params, 'steps.pre_existing_conditions.title'),
  } as const
  const yesNo = {
    type: 'yesNo',
    shouldTrackValues: true,
    shouldAutoSubmit: false,
    variant: 'filled',
    name: hasConditionsPath,
  } as const
  const info = {
    type: 'infoBox',
    showInfoIcon: true,
    contentConfiguration: [
      {
        type: 'text',
        text: v2Text('steps.pre_existing_conditions.info'),
        ...(platform === 'native' ? NATIVE_INFO_TEXT : {}),
      },
    ],
  } as const
  const conditionList = {
    type: 'itemActionList',
    name: conditionsPath,
    itemTitleKey: 'health_status_diagnosis',
    itemSubtitleKey: 'health_status_area',
    itemTitleTranslationKeyPrefix: 'sharedFlows_petHealthV2:conditions.illnesses.',
    itemSubtitleTranslationKeyPrefix: 'sharedFlows_petHealthV2:conditions.areas.',
    itemIconName: 'Bandaids',
    actionTransientPath: getTransientPath('condition_action'),
    addItemLabel: v2Text('steps.pre_existing_conditions.add'),
    addActionLabel: v2Text('steps.pre_existing_conditions.addAction'),
    editItemLabel: v2Text('steps.pre_existing_conditions.edit'),
    condition: { type: 'value', valuePath: hasConditionsPath, expectedValue: true },
  } as const

  return {
    stepContent:
      platform === 'native'
        ? [
            v2PetPortrait(params),
            title,
            yesNo,
            {
              ...info,
              condition: {
                type: 'value',
                valuePath: hasConditionsPath,
                equalityType: 'notEquals',
                expectedValue: true,
              },
            },
            conditionList,
          ]
        : [title, info, yesNo, conditionList],
    schema: schemaUtils.generateZodObjectFromPaths({
      [hasConditionsPath]: z.boolean(),
      [getTransientPath('condition_action')]: z.unknown().optional(),
    }),
    onPrepare: {
      type: 'simpleHook',
      callback: (flowValues: FlowValues) => ({
        formValues: { [conditionsPath]: listedConditions(params, flowValues) },
      }),
    },
    onSubmit: {
      type: 'simpleHook',
      callback: (flowValues: FlowValues) => ({
        formValues: {
          [getTransientPath('condition')]: null,
          [getTransientPath('condition_edit_index')]: null,
          ...(_get(flowValues, hasConditionsPath) === false ? { [conditionsPath]: [] } : {}),
        },
      }),
    },
    nextStepConfig: {
      valueFunction: resolveStepAfterConditionsQuestion(params),
      valueToStepName: { condition_area: 'condition_area', current_health: 'current_health' },
    },
    subFlow: params.subFlow,
    ...v2ContinueFooter(platform),
  } as const satisfies AnyStepConfiguration
}

export default pre_existing_conditions
